import { useEffect, useState, type ReactNode } from "react";
import { IconMessage2 } from "@tabler/icons-react";
import { Button } from "@onengine/component-library";
import { useAssignmentsAssistantStore } from "@/store/form-builder/useAssignmentsAssistantStore";
import { useFormAssistantAccess } from "@/components/features/FormBuilder/Assistant/useFormAssistantAccess";
import { AssistantDock } from "@/components/features/FormBuilder/Assistant/AssistantDock";
import { AssignmentsAssistantPanel } from "./AssignmentsAssistantPanel";
import type { AssignmentsTurnContext } from "@/store/form-builder/useAssignmentsAssistantStore";
import styles from "./AssignmentsAssistant.module.scss";

interface AssignmentsAssistantLayoutProps {
  tenantId?: string;
  focus?: AssignmentsTurnContext["focus"];
  scopeLabel?: string;
  children: ReactNode;
}

export function AssignmentsAssistantLayout({
  tenantId,
  focus,
  scopeLabel,
  children,
}: AssignmentsAssistantLayoutProps) {
  const canUseAssistant = useFormAssistantAccess();
  const isOpen = useAssignmentsAssistantStore((s) => s.isOpen);
  // The all-companies view has no company of its own, so the panel asks for one;
  // coming back to it keeps the company the conversation is about.
  const [pickedTenantId, setPickedTenantId] = useState<string | undefined>(() =>
    tenantId ? undefined : useAssignmentsAssistantStore.getState().tenantId,
  );
  const activeTenantId = tenantId ?? pickedTenantId;

  // A question asked with no company selected was about one: it becomes the
  // company picked, so the next questions and any changes are for it.
  const answeredFor = useAssignmentsAssistantStore((s) => s.answeredFor);
  useEffect(() => {
    if (!tenantId && answeredFor) setPickedTenantId(answeredFor.id);
  }, [tenantId, answeredFor]);

  // A conversation about one company must not carry over to another; moving
  // between pages of the same company keeps it. A page that is still loading its
  // company (no id yet) is not a switch.
  useEffect(() => {
    const owner = useAssignmentsAssistantStore.getState().tenantId;
    if (!activeTenantId || !owner || owner === activeTenantId) return;
    useAssignmentsAssistantStore.getState().reset();
  }, [activeTenantId]);

  return (
    <div className={styles.layout}>
      <div className={styles.main}>{children}</div>
      {canUseAssistant && isOpen && (
        <AssistantDock>
          <AssignmentsAssistantPanel
            tenantId={activeTenantId}
            focus={focus}
            scopeLabel={scopeLabel}
            companyPicker={
              tenantId
                ? undefined
                : {
                    value: pickedTenantId,
                    onChange: setPickedTenantId,
                    known: answeredFor ?? undefined,
                  }
            }
          />
        </AssistantDock>
      )}
    </div>
  );
}

export function AssignmentsAssistantButton() {
  const canUseAssistant = useFormAssistantAccess();
  const isOpen = useAssignmentsAssistantStore((s) => s.isOpen);
  const setOpen = useAssignmentsAssistantStore((s) => s.setOpen);
  if (!canUseAssistant) return null;
  return (
    <Button
      variant="secondary"
      size="small"
      className={isOpen ? styles.toggleOn : undefined}
      iconLeft={<IconMessage2 size={16} />}
      onClick={() => setOpen(!isOpen)}
      htmlAttributes={{ "aria-pressed": isOpen }}
    >
      Assistant
    </Button>
  );
}
